import React, { useCallback, useRef } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  TtIcon,
  TtDropdown,
  TtMenu,
  TtMenuLabel,
  TtMenuItem,
  TtFlavorContext,
} from '@23/ui-react'

import { useAllFeedsGrouped } from '../../../firebase/hooks/useFloor'
import {
  updateEditingMix,
  useEditingMixState,
} from '../../../firebase/hooks/useMixes'
import type { Mix, Feed, FeedMedia } from '../../../firebase/model/mix'
import { copy } from '../../../global/utils/valtio'
import { posToStyle } from '../../layouts'

import './EmptyMixSlots.scss'

const EmptyMixSlots = () => {
  const mix = useEditingMixState()

  const handleMediaChange = useCallback(
    (idx) => (newFeed: FeedMedia | undefined) => {
      const newMix = copy(mix) as Mix
      if (newFeed) {
        newMix.feeds.forEach((f, fIdx, array) => {
          if (
            f.media &&
            f.media.type === newFeed.type &&
            (f.media.type === 'tray' || f.media.id === newFeed.id)
          ) {
            // eslint-disable-next-line no-param-reassign
            array[fIdx].media = undefined
          }
        })
      }
      newMix.feeds[idx].media = newFeed
      updateEditingMix(newMix)
    },
    [mix]
  )

  if (!mix) return null

  return (
    <>
      {mix.feeds.map((f, idx) => {
        if (f.media) return null

        return (
          <Slot
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            feed={f}
            onChange={handleMediaChange(idx)}
          />
        )
      })}
    </>
  )
}

interface SlotProps {
  feed: Feed
  onChange?: (newFeed: Feed['media']) => void
}

const Slot = ({ feed, onChange }: SlotProps) => {
  const dropdown = useRef<HTMLTtDropdownElement>(null)
  const feeds = useAllFeedsGrouped()

  return (
    <button
      type="button"
      className="floor-empty-mix-slot"
      onClick={(event) => {
        if (event.target === event.currentTarget) void dropdown.current?.show()
      }}
      style={posToStyle(feed.position)}
    >
      <div className="floor-empty-mix-slot-placeholder">
        <TtIcon library="one" name="plus" />
        <div>Add media</div>
      </div>
      <TtFlavorContext flavor="webinars">
        <TtDropdown ref={dropdown} hoist closeOnSelect placement="right">
          <TtMenu
            // popover TODO: remove TtDropdown and use popover instead
            onTtSelect={(event) => {
              const newFeedMedia = event.detail.item.value.split('|')

              onChange?.({
                type: newFeedMedia[0] as NonNullable<Feed['media']>['type'],
                id: +newFeedMedia[1],
              })
            }}
          >
            {feeds.tray.length > 0 && (
              <>
                {feeds.tray.map((f) => (
                  <TtMenuItem key={f.id} value={`tray|${f.id}`}>
                    <TtIcon
                      slot="prefix"
                      library="one/webinars"
                      name="tray-slides"
                    />
                    Tray
                  </TtMenuItem>
                ))}
              </>
            )}
            {feeds.camera.length > 0 && (
              <>
                <TtMenuLabel>
                  <FormattedMessage defaultMessage="Speakers" />
                </TtMenuLabel>
                {feeds.camera.map((f) => (
                  <TtMenuItem key={f.id} value={`camera|${f.id}`}>
                    <TtIcon
                      slot="prefix"
                      library="one/webinars"
                      name="camera-on"
                    />
                    {f.name}
                  </TtMenuItem>
                ))}
              </>
            )}
            {feeds.screen.length > 0 && (
              <>
                <TtMenuLabel>
                  <FormattedMessage defaultMessage="Screen Sharing" />
                </TtMenuLabel>
                {feeds.screen.map((f) => (
                  <TtMenuItem key={f.id} value={`screen|${f.id}`}>
                    <TtIcon
                      slot="prefix"
                      library="one/webinars"
                      name="screen-share"
                    />
                    {f.name}
                  </TtMenuItem>
                ))}
              </>
            )}
            {feeds.mobile.length > 0 && (
              <>
                <TtMenuLabel>
                  <FormattedMessage defaultMessage="2ndCam" />
                </TtMenuLabel>
                {feeds.mobile.map((f) => (
                  <TtMenuItem key={f.id} value={`mobile|${f.id}`}>
                    {f.id}
                  </TtMenuItem>
                ))}
              </>
            )}
          </TtMenu>
        </TtDropdown>
      </TtFlavorContext>
    </button>
  )
}

export { EmptyMixSlots }
